import React from 'react';
import { Link } from 'react-router-dom';

import PublicSiteShell from './PublicSiteShell.jsx';
import { appUrl } from '../../lib/hosts.js';

const products = [
  {
    id: 'export-cloud',
    name: '长戈出口决策云',
    status: '已上线',
    summary: '面向出口企业的核心经营工作台，覆盖客户、产品、成本、退税、报价、审批、PDF 交付和经营复盘。',
    points: ['报价利润测算', '客户与产品库', '审批全程留痕', 'PDF 交付和公开分享'],
    href: appUrl('/login'),
    action: '进入出口决策云',
    featured: true
  },
  {
    id: 'factory-cost',
    name: '长戈工厂成本云',
    status: '产品占位',
    summary: '作为出口决策云的成本侧延展，面向制造型企业沉淀 BOM、工序、物料、人工和制造费用等真实成本口径。',
    points: ['多级 BOM', '工序库', '物料成本', '制造费用拆解'],
    href: '#factory-cost',
    action: '能力规划中'
  },
  {
    id: 'trade-growth',
    name: '长戈外贸增长云',
    status: '产品占位',
    summary: '作为出口决策云的增长侧延展，面向外贸团队沉淀客户分层、报价跟进、成交复盘和团队协同能力。',
    points: ['客户分组', '报价跟进', '成交分析', '团队看板'],
    href: '#trade-growth',
    action: '能力规划中'
  }
];

const layers = [
  ['客户', '客户分层、联系人、历史报价、跟进记录'],
  ['产品', '产品库、BOM、工序、物料成本、双语资料'],
  ['报价', '汇率、退税、费用分摊、毛利敏感性、审批'],
  ['交付', 'PDF、公开分享、移动审批、审计记录']
];

const focusItems = [
  ['客户与机会', '客户分层、联系人、报价历史与跟进记录统一沉淀，减少团队交接时的信息损耗。'],
  ['产品与成本', '产品库、BOM、工序、物料成本和双语资料形成可复用资产，让报价不再从空白表格开始。'],
  ['报价与利润', '汇率、退税、费用分摊、目标毛利和让利空间在报价前完成判断，减少拍脑袋报价。'],
  ['审批与交付', '老板审批、移动确认、PDF 输出和公开分享全程留痕，让内部确认和对外交付都有依据。']
];

function ProductDiagram() {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-950">出口决策链路</div>
          <div className="mt-1 text-xs text-slate-500">从客户到交付，关键数据在同一套系统里流转</div>
        </div>
        <div className="rounded-md bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">可复盘</div>
      </div>
      <div className="grid gap-3">
        {layers.map(([title, text], idx) => (
          <div key={title} className="grid grid-cols-[2.5rem_1fr] items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-slate-950 text-sm font-semibold text-white">
              {idx + 1}
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-sm font-semibold text-slate-900">{title}</div>
              <div className="mt-1 text-xs leading-5 text-slate-600">{text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Product() {
  return (
    <PublicSiteShell>
      <section className="bg-[#f7f3ec]">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-[1.05fr_.95fr] lg:items-center">
          <div>
            <p className="text-sm font-semibold text-chango-700">PRODUCTS</p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight text-slate-950 sm:text-5xl">
              长戈产品体系
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-700">
              长戈出口云先从出口决策云切入，解决出口企业最直接的报价、成本、退税和审批问题；工厂成本云与外贸增长云作为同一产品体系下的延展方向，保留清晰的产品占位。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={appUrl('/login')} className="rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                进入长戈出口决策云
              </a>
              <Link to="/pricing" className="rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                查看版本和价格
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              当前可正式进入应用的是长戈出口决策云，另外两个产品方向将随客户场景逐步开放。
            </p>
          </div>
          <ProductDiagram />
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-amber-700">产品结构</p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-950">一个已上线产品，两个产品占位。</h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-slate-600">
              产品页只表达当前真实能力和明确的扩展方向。出口决策云负责当下交付，工厂成本云和外贸增长云保留产品边界。
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            {products.map((p) => (
              <article
                key={p.id}
                id={p.id}
                className={[
                  'rounded-lg border p-6 shadow-sm',
                  p.featured ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-white text-slate-950'
                ].join(' ')}
              >
                <div className="mb-5 flex items-center justify-between gap-3">
                  <span className={[
                    'rounded-md px-3 py-1 text-xs font-semibold',
                    p.featured ? 'bg-white text-slate-950' : 'bg-slate-100 text-slate-700'
                  ].join(' ')}>
                    {p.status}
                  </span>
                </div>
                <h3 className="text-xl font-semibold">{p.name}</h3>
                <p className={['mt-4 min-h-[5.25rem] text-sm leading-7', p.featured ? 'text-slate-300' : 'text-slate-600'].join(' ')}>
                  {p.summary}
                </p>
                <ul className="mt-5 grid gap-2 text-sm">
                  {p.points.map((point) => (
                    <li key={point} className="flex items-center gap-2">
                      <span className={['h-1.5 w-1.5 rounded-full', p.featured ? 'bg-amber-300' : 'bg-chango-600'].join(' ')} />
                      <span className={p.featured ? 'text-slate-100' : 'text-slate-700'}>{point}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={p.href}
                  className={[
                    'mt-8 inline-flex w-full items-center justify-center rounded-md px-4 py-3 text-sm font-semibold',
                    p.featured ? 'bg-white text-slate-950 hover:bg-slate-100' : 'border border-slate-300 text-slate-900 hover:bg-slate-50'
                  ].join(' ')}
                >
                  {p.action}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[#fbfaf7] py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-8 md:grid-cols-[.85fr_1.15fr] md:items-start">
            <div>
              <p className="text-sm font-semibold text-chango-700">当前产品能力</p>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">先把出口决策云做深。</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                长戈出口决策云不做泛 CRM，也不做泛财务系统。它聚焦出口企业从客户、产品到报价审批的核心经营链路，把影响利润和成交的关键数据沉淀下来。
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {focusItems.map(([title, text]) => (
                <div key={title} className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="text-sm font-semibold text-slate-950">{title}</div>
                  <div className="mt-2 text-sm leading-6 text-slate-600">{text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PublicSiteShell>
  );
}
